import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  SkeletonBone,
  SkeletonCard,
  SkeletonGroup,
  SkeletonList,
  SkeletonSection,
} from '@/components/common/skeleton';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RADIUS } from '@/constants/shared/theme';

/** Rows per placeholder cadence section (reads as Daily ×3, Weekly ×2). */
const SECTION_ROW_COUNTS = [3, 2] as const;

/**
 * Loading placeholder for the To Do's index, shared by the mobile list and the
 * desktop view — both drop it into the slot the cadence sections occupy once
 * data lands. It mirrors the real geometry so content arrives without a jump:
 * an uppercase cadence header (12pt text, 15pt line box) above `TodoListRow`-
 * shaped cards (GlassCard defaults: 24 padding / 20 radius / 1pt border; title +
 * target chip + Edit on the first line, cadence + progress on the second). The
 * row boxes are pinned to the real line boxes (Edit button 28pt, progress line
 * 17pt) so a skeleton card is the same 105pt tall as a rendered row. No outer
 * padding — both hosts pad their scroll content. One SkeletonGroup so every bone
 * shares a single pulse.
 */
export const ToDosScreenSkeleton: React.FC = () => (
  <SkeletonGroup style={styles.container}>
    {SECTION_ROW_COUNTS.map((rowCount, i) => (
      <SkeletonSection key={i} headerWidth={56} headerHeight={14} gap={11}>
        <SkeletonList count={rowCount} gap={10}>
          <SkeletonCard
            padding={REDESIGN_SPACING.cardPadding}
            borderRadius={REDESIGN_SPACING.cardBorderRadius}
            style={styles.card}
          >
            <View style={styles.rowHeader}>
              <SkeletonBone width="55%" height={16} />
              <View style={styles.rowHeaderRight}>
                <SkeletonBone width={64} height={20} borderRadius={10} />
                <SkeletonBone width={50} height={28} borderRadius={RADIUS.sm} />
              </View>
            </View>
            <View style={styles.rowMeta}>
              <SkeletonBone width={48} height={13} />
              <SkeletonBone width={28} height={14} />
            </View>
          </SkeletonCard>
        </SkeletonList>
      </SkeletonSection>
    ))}
  </SkeletonGroup>
);

const styles = StyleSheet.create({
  // Matches the hosts' `scrollContent` gap between cadence sections.
  container: { gap: 20 },
  // GlassCard draws a 1pt border; keep the same box so card heights match.
  card: { borderWidth: 1, borderColor: 'transparent' },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  rowHeaderRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rowMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    // The real row is as tall as its 14pt bold progress text's line box.
    minHeight: 17,
  },
});
